import React, { useEffect, useState } from "react";
import { useI18n } from "@/i18n";
import { useSettings } from "@/context/SettingsContext";
import { useTags } from "@/context/TagsContext";
import { useAuth } from "@/context/AuthContext";
import { TagIcon, tagChipStyle } from "@/lib/tagStyles";
import api from "@/lib/api";
import MeritCard from "@/components/MeritCard";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Info, Users, Sparkles } from "lucide-react";

const META = {
  daily_merit: { zh: "每日功德", en: "Daily Merit", descZh: "每日一份善念，供灯、放生、印经…参与功德，累积福报，照亮当下。", descEn: "A good deed each day — lamp offerings, life release, sutra printing and more. Join and accumulate blessings.", showGroup: true },
  merit_project: { zh: "功德项目", en: "Merit Projects", descZh: "供灯、放生、印经…参与功德项目，共结善缘。", descEn: "Lamp offerings, life release, sutra printing — join and share good affinities.", showGroup: false },
  charity: { zh: "慈善爱心", en: "Charity", descZh: "参与爱心活动，把温暖送到需要的人手中。", descEn: "Join charity activities and deliver warmth to those in need.", showGroup: false },
  shop: { zh: "功德店铺", en: "Merit Shop", descZh: "结缘开运好物，护佑平安。每件请购皆可累积功德点数。", descEn: "Auspicious items for blessing and protection. Every purchase earns merit points.", showGroup: false },
};

export default function CategoryListing({ category, categories }) {
  const { lang, t } = useI18n();
  const { settings } = useSettings();
  const { tagsFor } = useTags();
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [tagFilter, setTagFilter] = useState("all");
  const [recs, setRecs] = useState(null); // {recommendations, needed_labels}
  const meta = META[category];
  const wa = settings?.whatsapp || {};
  const catKey = Array.isArray(categories) && categories.length ? categories.join(",") : category;

  useEffect(() => {
    setItems(null);
    setTagFilter("all");
    const params = Array.isArray(categories) && categories.length
      ? { categories: categories.join(",") }
      : { category };
    api.get("/projects", { params }).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [catKey]);

  // Personalised five-element product match (shop only, logged-in members).
  // Guests / incomplete profiles simply see the full catalogue with no rec block.
  useEffect(() => {
    setRecs(null);
    if (category !== "shop" || !user) return;
    api.get("/chart/match", { params: { category: "shop", lang } })
      .then((r) => {
        if (r.data?.profile_complete && (r.data.recommendations || []).length > 0) setRecs(r.data);
      })
      .catch(() => { /* silently skip recommendations */ });
  }, [category, user, lang]);

  // present sub-category names that actually have items in this section
  const present = new Set((items || []).map((p) => p.subcategory).filter(Boolean));
  // ordered tag definitions for this section, limited to ones with items…
  const catList = Array.isArray(categories) && categories.length ? categories : [category];
  const orderedTags = tagsFor(catList).filter((tg) => present.has(tg.name));
  // …plus any used tag that has no definition yet (fallback), appended at end
  const definedNames = new Set(orderedTags.map((t) => t.name));
  const extraTags = Array.from(present).filter((n) => !definedNames.has(n)).map((n) => ({ name: n, color: "", icon: "" }));
  const tags = [...orderedTags, ...extraTags];
  const visible = (items || []).filter((p) => tagFilter === "all" || p.subcategory === tagFilter);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.25em] text-[hsl(var(--brand-bronze))] font-semibold">{category.replace("_", " ").toUpperCase()}</div>
        <h1 className="mt-2 font-display text-3xl sm:text-4xl font-bold">{lang === "zh" ? meta.zh : meta.en}</h1>
        <p className="mt-2 text-muted-foreground max-w-2xl">{lang === "zh" ? meta.descZh : meta.descEn}</p>
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--brand-jade-mist))] px-4 py-1.5 text-sm text-[hsl(var(--brand-ink))]">
          <Info className="h-4 w-4" />
          {lang === "zh" ? "消费 RM1 获得 0.01 功德点数" : "Spend RM1 to earn 0.01 merit points"}
        </div>
        {meta.showGroup && wa.group_link && (
          <Button asChild variant="outline" className="border-[#25D366] text-[#128C4B]" data-testid="join-group-button">
            <a href={wa.group_link} target="_blank" rel="noopener noreferrer">
              <Users className="h-4 w-4 mr-1" /> {t("common.joinGroup")}
            </a>
          </Button>
        )}
      </div>

      {recs && recs.recommendations?.length > 0 && (
        <section className="mb-8 rounded-2xl border border-[hsl(var(--brand-gold))]/40 bg-[hsl(var(--brand-jade-mist))]/40 p-4 sm:p-6" data-testid="shop-recommendations">
          <div className="flex items-center flex-wrap gap-2 mb-1.5">
            <Sparkles className="h-5 w-5 text-[hsl(var(--brand-bronze))]" />
            <h2 className="font-display text-xl sm:text-2xl font-semibold">{lang === "zh" ? "适合你的产品" : "Recommended for you"}</h2>
            {(recs.needed_labels || []).map((n) => (
              <Badge key={n.code} className="bg-[hsl(var(--brand-gold-soft))] text-[hsl(var(--brand-ink))]" data-testid={`shop-rec-element-${n.code}`}>
                {lang === "zh" ? "喜用" : "Favourable"} · {lang === "zh" ? n.zh : n.en}
              </Badge>
            ))}
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            {lang === "zh"
              ? "根据你的八字命盘，以下开运好物与你的喜用五行相合，宜补助运。"
              : "Based on your BaZi chart, these items align with your favourable five elements."}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="shop-rec-items">
            {recs.recommendations.map((p) => <MeritCard key={p.id} project={p} />)}
          </div>
        </section>
      )}

      {items !== null && tags.length > 0 && (
        <div className="mb-6 flex flex-wrap gap-2" data-testid="tag-filter-bar">
          <Button
            size="sm"
            variant={tagFilter === "all" ? "default" : "outline"}
            className={tagFilter === "all" ? "bg-[hsl(var(--primary))] text-primary-foreground rounded-full" : "rounded-full"}
            onClick={() => setTagFilter("all")}
            data-testid="tag-filter-all"
          >
            {lang === "zh" ? "全部" : "All"}
          </Button>
          {tags.map((tg) => (
            <Button
              key={tg.name}
              size="sm"
              variant="outline"
              className="rounded-full gap-1"
              style={tagChipStyle(tg.color, tagFilter === tg.name)}
              onClick={() => setTagFilter(tg.name)}
              data-testid={`tag-filter-${tg.name}`}
            >
              {tg.icon && <TagIcon name={tg.icon} />}
              {tg.name}
            </Button>
          ))}
        </div>
      )}

      {items === null ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-80 rounded-xl" />)}
        </div>
      ) : visible.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">{lang === "zh" ? "暂无项目" : "No items yet"}</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {visible.map((p) => <MeritCard key={p.id} project={p} />)}
        </div>
      )}
    </div>
  );
}
